/**
 * Narrowing a review list by text and by star rating.
 *
 * Pure and separate from the panel so the rules can be tested without
 * rendering anything, and so the one rule that matters is stated in one place:
 * a filter may only ever HIDE rows from the list it was handed. It must never
 * imply it searched more than that.
 *
 * The lists it is handed are capped server-side (answered at 200 of what can
 * be 1,559). The panel says so next to the box; this function's job is simply
 * not to pretend otherwise.
 */

export interface FilterableReview {
	reviewer?: string;
	comment?: string;
	rating?: number | null;
	reply?: string;
}

/** Case- and accent-insensitive, so "jose" finds "José". */
const fold = (value: string): string =>
	value
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();

export function filterReviews<T extends FilterableReview>(
	reviews: T[],
	query: string,
	stars: number | null,
): T[] {
	const needle = fold(query.trim());
	return reviews.filter((review) => {
		if (stars !== null && (review.rating ?? 0) !== stars) return false;
		if (!needle) return true;
		// The reply is searched too: "what did we say about the car park?" is
		// a real question, and on the answered tab the reply IS the content.
		const haystack = fold(
			`${review.reviewer ?? ""} ${review.comment ?? ""} ${review.reply ?? ""}`,
		);
		return haystack.includes(needle);
	});
}

/** The star counts present in a list, so we never offer an empty filter. */
export function starCounts(reviews: FilterableReview[]): Map<number, number> {
	const counts = new Map<number, number>();
	for (const review of reviews) {
		const rating = review.rating ?? 0;
		if (rating >= 1 && rating <= 5) {
			counts.set(rating, (counts.get(rating) ?? 0) + 1);
		}
	}
	return counts;
}

/** How a review list is ordered. */
export type ReviewSort = "rating" | "newest" | "oldest";

export interface SortableReview extends FilterableReview {
	dateCreated?: string;
	replyDate?: string;
}

/**
 * Order a review list.
 *
 * "rating" is worst-first and is the default for the reply worklist, because
 * the reply that protects revenue is the one under the one-star, not the
 * newest five-star. Date ordering was asked for on top of that, not instead
 * of it, so it is an option rather than a replacement.
 *
 * Sorting is by the review's OWN date, not the reply's, on both halves. An
 * operator asking "what is the oldest review still unanswered?" means the
 * review, and using reply_date on the answered half would silently answer a
 * different question.
 *
 * Returns a new array; the caller's list is never mutated.
 */
export function sortReviews<T extends SortableReview>(
	reviews: T[],
	sort: ReviewSort,
): T[] {
	const copy = [...reviews];
	if (sort === "rating") {
		// Missing rating sorts last, not first: an unknown is not a one-star.
		return copy.sort((a, b) => (a.rating ?? 99) - (b.rating ?? 99));
	}
	const key = (r: T): string => r.dateCreated ?? "";
	return copy.sort((a, b) => {
		const x = key(a);
		const y = key(b);
		// A review with no date sorts last whichever way round we are going —
		// it is not evidence of being the oldest or the newest.
		if (!x && !y) return 0;
		if (!x) return 1;
		if (!y) return -1;
		return sort === "newest" ? y.localeCompare(x) : x.localeCompare(y);
	});
}
